import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import { useAction } from "wasp/client/operations";
import { deleteWalletWithdrawal, updateWalletWithdrawalStatus } from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../client/components/ui/select";
import useTranslation from "../client/hooks/useTranslation";

interface Withdrawal {
  id: string;
  date: Date | string;
  amount: number;
  method: string | null;
  status: "pending" | "completed";
  notes: string | null;
}

interface WalletWithdrawalsTableProps {
  withdrawals: Withdrawal[];
  onMutated: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  completed: "bg-primary/15 text-primary border-primary/30",
  pending: "bg-amber-400/15 text-amber-500 border-amber-400/30",
};

export function WalletWithdrawalsTable({ withdrawals, onMutated }: WalletWithdrawalsTableProps) {
  const t = useTranslation();
  const w = t.propFirmManager.wallet;
  const deleteWithdrawal = useAction(deleteWalletWithdrawal);
  const updateStatus = useAction(updateWalletWithdrawalStatus);

  const handleDelete = async (withdrawalId: string) => {
    if (!window.confirm(w.deleteWithdrawalConfirm)) return;
    await deleteWithdrawal({ withdrawalId });
    onMutated();
  };

  const handleStatusChange = async (withdrawalId: string, status: string) => {
    await updateStatus({ withdrawalId, status: status as "pending" | "completed" });
    onMutated();
  };

  if (withdrawals.length === 0) {
    return <p className="text-xs text-muted-foreground py-4 text-center">{w.noWithdrawals}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-left pb-2 pr-3 font-medium">{w.colDate}</th>
            <th className="text-right pb-2 pr-3 font-medium">{w.colAmount}</th>
            <th className="text-left pb-2 pr-3 font-medium">{w.colMethod}</th>
            <th className="text-left pb-2 pr-3 font-medium">{w.colStatus}</th>
            <th className="text-right pb-2 font-medium">{w.colActions}</th>
          </tr>
        </thead>
        <tbody>
          {withdrawals.map((wd) => (
            <tr key={wd.id} className="border-b border-border">
              <td className="py-2.5 pr-3 font-mono">
                {format(new Date(wd.date), "dd MMM yyyy")}
              </td>
              <td className="py-2.5 pr-3 text-right font-mono font-semibold">
                <span className="text-pnlNegative">-${wd.amount.toFixed(2)}</span>
              </td>
              <td className="py-2.5 pr-3 text-muted-foreground">
                {wd.method ?? "—"}
              </td>
              <td className="py-2.5 pr-3">
                <Select
                  value={wd.status}
                  onValueChange={(v) => handleStatusChange(wd.id, v)}
                >
                  <SelectTrigger className={`h-6 text-xs border px-2 w-28 ${STATUS_BADGE[wd.status]}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{w.statusPending}</SelectItem>
                    <SelectItem value="completed">{w.statusCompleted}</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="py-2.5 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(wd.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
